import React, { useState } from 'react';
import './FeedbackPage.css';
import {
  useGetAdminFeedbackQuery,
  useUpdateFeedbackStatusMutation,
  FeedbackItem,
  FeedbackType,
  FeedbackStatus
} from 'store/services/feedback/feedbackApi';
import AdminLayout from '../AdminLayout';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { ChatImageLightbox } from 'components/ChatImageLightbox/ChatImageLightbox';

// Status configuration with colors and labels
const FEEDBACK_STATUSES: { value: FeedbackStatus; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: '#ef4444' },           // Red
  { value: 'read', label: 'Read', color: '#f97316' },         // Orange
  { value: 'in_progress', label: 'In Progress', color: '#eab308' }, // Yellow
  { value: 'live', label: 'Live', color: '#22c55e' },         // Green
  { value: 'done', label: 'Done', color: '#3b82f6' },         // Blue
];

// Default active status filters (Done is OFF by default)
const DEFAULT_ACTIVE_STATUSES: FeedbackStatus[] = ['new', 'read', 'in_progress', 'live'];

// Helper to get status color
const getStatusColor = (status: FeedbackStatus) => {
  const found = FEEDBACK_STATUSES.find(s => s.value === status);
  return found?.color || '#999';
};

const FeedbackPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<FeedbackType | 'all'>('all');
  const [page, setPage] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [activeStatusFilters, setActiveStatusFilters] = useState<FeedbackStatus[]>(DEFAULT_ACTIVE_STATUSES);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<{ id: string; url: string }[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isModalUpdating, setIsModalUpdating] = useState(false);
  const [modalUpdateTarget, setModalUpdateTarget] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const { data, isLoading, error } = useGetAdminFeedbackQuery({
    page,
    pageSize: 50,
    search: searchTerm,
    feedbackType: feedbackTypeFilter === 'all' ? undefined : feedbackTypeFilter
  });

  const [updateFeedbackStatus] = useUpdateFeedbackStatusMutation();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFeedbackTypeLabel = (type: FeedbackType) => {
    switch (type) {
      case 'bug_report':
        return 'Bug Report';
      case 'feature_request':
        return 'Feature Request';
      case 'general':
        return 'General';
      default:
        return type;
    }
  };

  const getFeedbackTypeColor = (type: FeedbackType) => {
    switch (type) {
      case 'bug_report':
        return '#ef4444'; // Red
      case 'feature_request':
        return '#3b82f6'; // Blue  
      case 'general':
        return '#10b981'; // Green
      default:
        return '#999';
    }
  };

  const getSentimentEmoji = (sentiment?: string) => {
    switch (sentiment) {
      case 'frustrated':
        return '😤';
      case 'neutral':
        return '😐';
      case 'happy':
        return '😊';
      default:
        return '';
    }
  };

  const getOverviewText = (item: FeedbackItem) => {
    // Combine all fields for bug reports and feature requests
    if (item.feedbackType === 'bug_report') {
      const parts = [];
      if (item.overview) parts.push(`Overview: ${item.overview}`);
      if (item.currentBehavior) parts.push(`Current: ${item.currentBehavior}`);
      if (item.desiredBehavior) parts.push(`Desired: ${item.desiredBehavior}`);
      return parts.length > 0 ? parts.join(' | ') : 'No overview provided';
    } else if (item.feedbackType === 'feature_request') {
      const parts = [];
      if (item.featureOverview) parts.push(`Overview: ${item.featureOverview}`);
      if (item.coreProblem) parts.push(`Problem: ${item.coreProblem}`);
      if (item.proposedSolution) parts.push(`Solution: ${item.proposedSolution}`);
      return parts.length > 0 ? parts.join(' | ') : 'No overview provided';
    } else {
      return item.generalFeedback || 'No feedback provided';
    }
  };

  // Handle status dropdown change
  const handleStatusChange = async (itemId: string, newStatus: FeedbackStatus) => {
    setUpdatingStatusId(itemId);
    try {
      await updateFeedbackStatus({ id: itemId, status: newStatus }).unwrap();
      // If we have selectedFeedback open and it's the same item, update it
      if (selectedFeedback && selectedFeedback.id === itemId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Handle View button click - auto-update from "new" to "read"
  const handleViewFeedback = async (item: FeedbackItem) => {
    setSelectedFeedback(item);
    setIsDetailModalOpen(true);

    // Auto-update status from "new" to "read" when viewing
    if (item.status === 'new' || !item.status) {
      try {
        await updateFeedbackStatus({ id: item.id, status: 'read' }).unwrap();
      } catch (error) {
        console.error('Failed to auto-update status to read:', error);
      }
    }
  };

  // Handle status update from modal action buttons
  const handleModalStatusUpdate = async (status: FeedbackStatus) => {
    if (!selectedFeedback) return;
    setIsModalUpdating(true);
    setModalUpdateTarget(status);
    try {
      await updateFeedbackStatus({ id: selectedFeedback.id, status }).unwrap();
      setSelectedFeedback({ ...selectedFeedback, status });
      setIsDetailModalOpen(false);
    } catch (err: any) {
      alert(err?.data?.message || err?.message || 'Failed to update status');
    } finally {
      setIsModalUpdating(false);
      setModalUpdateTarget(null);
    }
  };

  // Copy feedback details to clipboard
  const handleCopyFeedback = async () => {
    if (!selectedFeedback) return;

    const lines: string[] = ['Feedback Details', ''];
    lines.push('User:', selectedFeedback.user?.username || 'Unknown User', selectedFeedback.user?.email || 'No email', '');
    lines.push('Type:', getFeedbackTypeLabel(selectedFeedback.feedbackType), '');
    lines.push('Submitted:', formatDate(selectedFeedback.createdAt), '');

    if (selectedFeedback.feedbackType === 'bug_report') {
      lines.push('Overview:', selectedFeedback.overview || 'No overview provided', '');
      lines.push('Current Behavior:', selectedFeedback.currentBehavior || 'Not provided', '');
      lines.push('Desired Behavior:', selectedFeedback.desiredBehavior || 'Not provided', '');
    } else if (selectedFeedback.feedbackType === 'feature_request') {
      lines.push('Feature Overview:', selectedFeedback.featureOverview || 'No overview provided', '');
      lines.push('Core Problem:', selectedFeedback.coreProblem || 'Not provided', '');
      lines.push('Proposed Solution:', selectedFeedback.proposedSolution || 'Not provided', '');
    } else {
      lines.push('Sentiment:', `${getSentimentEmoji(selectedFeedback.sentiment)} ${selectedFeedback.sentiment || 'N/A'}`, '');
      lines.push('Feedback:', selectedFeedback.generalFeedback || 'No feedback provided', '');
      lines.push('Response Preference:', selectedFeedback.responsePreference || 'No preference', '');
    }

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2000);
    }
  };

  // Toggle status filter pill
  const handleStatusFilterToggle = (status: FeedbackStatus) => {
    setActiveStatusFilters(prev => {
      if (prev.includes(status)) {
        // Don't allow removing all filters
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== status);
      }
      return [...prev, status];
    });
  };

  // Use real backend data
  const rawFeedback = data?.feedback || [];

  // Apply filters to the feedback data
  const feedback = rawFeedback.filter((item: FeedbackItem) => {
    // Filter by active status filters (default to 'new' if no status)
    // Normalize to lowercase to handle backend returning uppercase values
    const itemStatus = (item.status?.toLowerCase() || 'new') as FeedbackStatus;
    if (!activeStatusFilters.includes(itemStatus)) {
      return false;
    }

    // Filter by feedback type
    if (feedbackTypeFilter !== 'all' && item.feedbackType !== feedbackTypeFilter) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesUsername = item.user?.username?.toLowerCase().includes(searchLower);
      const matchesEmail = item.user?.email?.toLowerCase().includes(searchLower);
      const matchesOverview =
        item.overview?.toLowerCase().includes(searchLower) ||
        item.featureOverview?.toLowerCase().includes(searchLower) ||
        item.generalFeedback?.toLowerCase().includes(searchLower) ||
        item.currentBehavior?.toLowerCase().includes(searchLower) ||
        item.desiredBehavior?.toLowerCase().includes(searchLower) ||
        item.coreProblem?.toLowerCase().includes(searchLower) ||
        item.proposedSolution?.toLowerCase().includes(searchLower);

      return matchesUsername || matchesEmail || matchesOverview;
    }

    return true;
  });

  return (
    <AdminLayout title="Admin Panel - Feedback" currentPath="/admin/feedback">
      {/* @ts-expect-error - React 19 types issue with children */}
      <div className="feedback-page">
        <div className="feedback-header">
          <h2 className="feedback-title">User Feedback</h2>
          <div className="feedback-controls">
            <input
              type="text"
              placeholder="Search by username or content..."
              className="feedback-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="feedback-filter-dropdown"
              value={feedbackTypeFilter}
              onChange={(e) => setFeedbackTypeFilter(e.target.value as FeedbackType | 'all')}
            >
              <option value="all">All Types</option>
              <option value="bug_report">Bug Reports</option>
              <option value="feature_request">Feature Requests</option>
              <option value="general">General Feedback</option>
            </select>

            {/* Status Filter Pills */}
            <div className="feedback-status-pills">
              {FEEDBACK_STATUSES.map(status => {
                const isActive = activeStatusFilters.includes(status.value);
                return (
                  <button
                    key={status.value}
                    className={`feedback-status-pill ${isActive ? 'active' : ''}`}
                    style={{
                      backgroundColor: isActive ? status.color : 'transparent',
                      borderColor: status.color,
                      color: isActive ? '#fff' : status.color,
                    }}
                    onClick={() => handleStatusFilterToggle(status.value)}
                  >
                    {status.label}
                    {isActive && <span className="feedback-status-pill-x">×</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="feedback-table-container">
          <table className="feedback-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Type</th>
                <th>Overview</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {feedback.map((item: FeedbackItem) => {
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="feedback-user-cell">
                        <AvatarWithFlag
                          avatarUrl={item.user?.avatar}
                          username={item.user?.username}
                          size={32}
                        />
                        <span>{item.user?.username || 'Unknown User'}</span>
                      </div>
                    </td>
                  <td>
                    <span className="feedback-email">{item.user?.email || 'No email'}</span>
                  </td>
                  <td>
                    <span 
                      className="feedback-type-badge"
                      style={{ backgroundColor: getFeedbackTypeColor(item.feedbackType) }}
                    >
                      {getFeedbackTypeLabel(item.feedbackType)}
                    </span>
                  </td>
                  <td>
                    <div className="feedback-overview">
                      {getOverviewText(item)}
                      {item.feedbackType === 'general' && item.sentiment && (
                        <span className="feedback-sentiment-inline">
                          {' '}{getSentimentEmoji(item.sentiment)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="feedback-date">
                      {formatDate(item.createdAt)}
                    </span>
                  </td>
                  <td>
                    <div className="feedback-status-dropdown-wrapper">
                      <select
                        className="feedback-status-dropdown"
                        value={item.status || 'new'}
                        onChange={(e) => handleStatusChange(item.id, e.target.value as FeedbackStatus)}
                        disabled={updatingStatusId === item.id}
                        style={{
                          backgroundColor: getStatusColor((item.status || 'new') as FeedbackStatus),
                        }}
                      >
                        {FEEDBACK_STATUSES.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                      {updatingStatusId === item.id && (
                        <span className="feedback-status-loading">...</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="feedback-actions">
                      <button
                        className="feedback-view-btn"
                        onClick={() => handleViewFeedback(item)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {feedback.length === 0 && !isLoading && (
          <div className="feedback-empty">
            No feedback found. Adjust your filters or check back later.
          </div>
        )}

        {isLoading && (
          <div className="feedback-loading">Loading feedback...</div>
        )}

        {error && (
          <div className="feedback-error">Error loading feedback. Please try again.</div>
        )}

        {/* Pagination */}
        {data && data.hasMore && (
          <div className="feedback-pagination">
            <button 
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">Page {page}</span>
            <button 
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={!data.hasMore}
            >
              Next
            </button>
          </div>
        )}

        {/* Feedback Detail Modal */}
        {isDetailModalOpen && selectedFeedback && (() => {
          return (
            <div className="feedback-detail-modal-overlay" onClick={() => setIsDetailModalOpen(false)}>
              <div className="feedback-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="feedback-detail-header">
                  <h3>Feedback Details</h3>
                  <div className="feedback-detail-header-actions">
                    <button
                      className={`feedback-detail-copy ${copyState !== 'idle' ? copyState : ''}`}
                      onClick={handleCopyFeedback}
                      title={copyState === 'copied' ? 'Copied!' : copyState === 'error' ? 'Failed to copy' : 'Copy to clipboard'}
                    >
                      {copyState === 'copied' ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8.5L6.5 12L13 4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : copyState === 'error' ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M4 4L12 12M12 4L4 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 11V3C3 2.44772 3.44772 2 4 2H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      )}
                    </button>
                    <button className="feedback-detail-close" onClick={() => setIsDetailModalOpen(false)}>
                      ×
                    </button>
                  </div>
                </div>
                <div className="feedback-detail-content">
                  <div className="feedback-detail-row">
                    <label>User:</label>
                    <div className="feedback-detail-user">
                      <AvatarWithFlag
                        avatarUrl={selectedFeedback.user?.avatar}
                        username={selectedFeedback.user?.username}
                        size={40}
                      />
                      <div>
                        <div>{selectedFeedback.user?.username || 'Unknown User'}</div>
                        <div className="feedback-detail-email">{selectedFeedback.user?.email}</div>
                      </div>
                    </div>
                  </div>
                
                <div className="feedback-detail-row">
                  <label>Type:</label>
                  <span className="feedback-type-badge" style={{ backgroundColor: getFeedbackTypeColor(selectedFeedback.feedbackType) }}>
                    {getFeedbackTypeLabel(selectedFeedback.feedbackType)}
                  </span>
                </div>

                <div className="feedback-detail-row">
                  <label>Submitted:</label>
                  <span>{formatDate(selectedFeedback.createdAt)}</span>
                </div>

                {/* Bug Report Fields */}
                {selectedFeedback.feedbackType === 'bug_report' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Overview:</label>
                      <p>{selectedFeedback.overview || 'No overview provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Current Behavior:</label>
                      <p>{selectedFeedback.currentBehavior || 'Not provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Desired Behavior:</label>
                      <p>{selectedFeedback.desiredBehavior || 'Not provided'}</p>
                    </div>
                  </>
                )}

                {/* Feature Request Fields */}
                {selectedFeedback.feedbackType === 'feature_request' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Feature Overview:</label>
                      <p>{selectedFeedback.featureOverview || 'No overview provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Core Problem:</label>
                      <p>{selectedFeedback.coreProblem || 'Not provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Proposed Solution:</label>
                      <p>{selectedFeedback.proposedSolution || 'Not provided'}</p>
                    </div>
                  </>
                )}

                {/* General Feedback Fields */}
                {selectedFeedback.feedbackType === 'general' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Sentiment:</label>
                      <span className="feedback-sentiment-large">
                        {getSentimentEmoji(selectedFeedback.sentiment)}
                        {' '}
                        {selectedFeedback.sentiment}
                      </span>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Feedback:</label>
                      <p>{selectedFeedback.generalFeedback || 'No feedback provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Response Preference:</label>
                      <span>{selectedFeedback.responsePreference || 'No preference'}</span>
                    </div>
                  </>
                )}

                {/* Image Attachments */}
                {selectedFeedback.images && selectedFeedback.images.length > 0 && (
                  <div className="feedback-detail-row">
                    <label>Attachments:</label>
                    <div className="feedback-detail-images">
                      {selectedFeedback.images.map((image, index) => (
                        <img
                          key={image.id}
                          src={image.url}
                          alt={`Attachment ${index + 1}`}
                          className="feedback-detail-thumbnail"
                          onClick={() => {
                            setLightboxImages(selectedFeedback.images!);
                            setLightboxIndex(index);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="feedback-detail-footer">
                  <button
                    className="feedback-detail-btn cancel"
                    onClick={() => setIsDetailModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="feedback-detail-btn in-progress"
                    onClick={() => handleModalStatusUpdate('in_progress')}
                    disabled={isModalUpdating}
                  >
                    {isModalUpdating && modalUpdateTarget === 'in_progress' ? 'Updating...' : 'In Progress'}
                  </button>
                  <button
                    className="feedback-detail-btn done"
                    onClick={() => handleModalStatusUpdate('done')}
                    disabled={isModalUpdating}
                  >
                    {isModalUpdating && modalUpdateTarget === 'done' ? 'Updating...' : 'Done'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {lightboxImages && (
          <ChatImageLightbox
            images={lightboxImages}
            initialIndex={lightboxIndex}
            onClose={() => setLightboxImages(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default FeedbackPage;